import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Code2,
  Cog,
  Users,
  Wrench,
  Workflow,
  ShieldCheck,
  PlugZap,
  Sliders,
  FileText,
  Lock,
  GitMerge,
  ClipboardList,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Code2,
    title: 'Built on Oracle ADF / JSF',
    detail:
      'The non-PO coding form in a typical WebCenter AP implementation is an Oracle Application Development Framework (ADF) page — JSF-based UI, built in JDeveloper, deployed to WebLogic Server alongside the WebCenter Imaging and SOA stack.',
  },
  {
    icon: ClipboardList,
    title: 'Codes invoices with no PO match',
    detail:
      'When there is no PO to match an invoice against, the AP user opens the coding form to assign charge accounts, cost centers, projects, funds, departments, locations, and any other dimensional posting required by the GL chart of accounts.',
  },
  {
    icon: Wrench,
    title: 'Customized over years',
    detail:
      'On most engagements the form has been customized over years — fund accounting rules, encumbrance logic, segment defaulting, validation hooks, custom Java in backing beans, lookup tables specific to that customer\'s chart of accounts.',
  },
  {
    icon: Workflow,
    title: 'Coupled to SOA composites',
    detail:
      'Submission from the form usually routes through SOA composites for approval workflow and on to EBS Payables Open Interface or Fusion FBDI / OIC for posting. Changes ripple into those downstream components.',
  },
];

const PAIN_POINTS = [
  {
    icon: Users,
    title: 'Changes need senior ADF hands-on the stack',
    detail:
      'Working confidently in JDeveloper, ADF Faces, ADF Business Components, and ADF Task Flows takes senior, hands-on experience. The customers we talk to describe long lead times to line up someone who can safely touch the page, and small changes waiting on that availability.',
  },
  {
    icon: Cog,
    title: 'Every coding rule change is a development cycle',
    detail:
      'Adding a new fund, changing a default cost center, updating a validation rule, or extending segment defaulting almost always requires opening JDeveloper, editing the page or a backing bean, rebuilding the EAR, and redeploying to WebLogic. AP teams cannot self-serve.',
  },
  {
    icon: Sliders,
    title: 'The UX has not aged well',
    detail:
      'ADF Faces pages designed in the early 2010s show their age — dense tables, limited keyboard support, browser compatibility quirks, and no real mobile story. AP teams that have adopted modern tooling elsewhere notice the gap when they enter the coding step.',
  },
  {
    icon: GitMerge,
    title: 'Tight coupling to downstream SOA composites',
    detail:
      'The form\'s submit handler, payload structure, and validation rules are often woven into SOA composites that route the coded invoice forward. Touching the form without an SOA-aware review risks breaking approval routing, encumbrance checks, or posting.',
  },
];

const SCENARIOS = [
  {
    title: 'Every small change becomes a development project',
    body:
      '"Our non-PO coding form is built in Oracle ADF and customizations turn every small change into a development project." This is the verbatim language we hear most often. AP wants a new fund or a tweaked default and the answer comes back as a JDeveloper sprint.',
  },
  {
    title: 'EBS to Fusion migration where the ADF form is the blocker',
    body:
      'The ERP team has a path from EBS to Fusion Cloud — but the ADF coding form is so customized that nobody can confidently answer what happens to it on the other side. The non-PO coding experience becomes the long pole that delays the migration plan.',
  },
  {
    title: 'Fund accounting or encumbrance rules outgrew the form',
    body:
      'Public-sector, healthcare, and education customers add new funds, grants, and encumbrance scenarios over time. The original ADF design assumed a simpler model. Each new requirement gets bolted on as another customization until the form is fragile to change.',
  },
  {
    title: 'Browser compatibility forcing an emergency JDeveloper rebuild',
    body:
      'A browser update or a managed-endpoint policy change breaks the ADF page rendering. The customer suddenly needs the ADF skill set on short notice, just to keep the existing form running, with no improvement in capability.',
  },
];

const DELIVERY_STEPS = [
  {
    step: '1',
    title: 'Read the existing ADF form configuration',
    body:
      'Andrew reads the existing ADF page, backing beans, ADF Business Components, custom Java, and any iDoc Script or workflow hooks that surround it. Extracts the coding rules, defaulting logic, validation behavior, and approval routing — captured as a structured inventory the customer keeps regardless of next step.',
  },
  {
    step: '2',
    title: 'Map coding rules to the EZ Cloud configurable coding engine',
    body:
      'Charge account segments, cost center defaulting, fund and project lookups, encumbrance rules, dimensional posting, and validation behavior are mapped into the EZ Cloud coding configuration. Where the ADF page held rules in custom Java, the equivalent rule becomes an AP-administrator-editable configuration entry.',
  },
  {
    step: '3',
    title: 'Preserve the EBS or Fusion integration pattern',
    body:
      'For EBS customers, the EZ Cloud platform writes the coded invoice to Payables Open Interface using the same column conventions the existing implementation uses. For Fusion customers, the platform posts via FBDI or REST through Oracle Integration Cloud — the same Oracle-supported interfaces the ERP team already audits.',
  },
  {
    step: '4',
    title: 'Re-wire approval routing',
    body:
      'Where the existing implementation routed through SOA composites, the EZ Cloud configurable approval engine takes over the routing decision and the SOA layer is either retired or simplified. Where the customer wants to keep an existing approval composite, the EZ Cloud platform can hand off to it.',
  },
  {
    step: '5',
    title: 'Parallel run during UAT',
    body:
      'The existing ADF form and the new EZ Cloud coding experience run in parallel during UAT. AP users code a sample set in both, and the resulting GL distribution lines are reconciled. Cutover happens only after end-to-end posting validation is complete.',
  },
  {
    step: '6',
    title: 'Hand the configuration to the AP team',
    body:
      'After cutover, the AP team owns the coding configuration through an admin UI. Adding a fund, updating a default, or extending a validation rule no longer requires JDeveloper, a developer, or a redeploy.',
  },
];

const WHAT_STAYS = [
  {
    icon: ShieldCheck,
    title: 'ERP system of record',
    detail:
      'Oracle EBS or Fusion remains the system of record. Vendor master, GL, payment processing, and period close stay where they are.',
  },
  {
    icon: FileText,
    title: 'Audit trail and posting integrity',
    detail:
      'Every coded invoice carries the same audit detail — who coded it, who approved it, when it was posted, and the full coding history. The audit story does not weaken in the move.',
  },
  {
    icon: Lock,
    title: 'Segregation of duties and security model',
    detail:
      'Roles, segregation between coder and approver, and the existing security boundaries are preserved. EZ Cloud roles map to the existing customer security model rather than replacing it.',
  },
  {
    icon: PlugZap,
    title: 'Oracle-supported integration interfaces',
    detail:
      'Integration into the ERP uses Oracle-supported interfaces — Payables Open Interface for EBS, FBDI and OIC REST for Fusion. No custom database writes, no unsupported back-channel.',
  },
];

const FAQS = [
  {
    q: 'What is the Oracle ADF non-PO coding form?',
    a: 'The Oracle ADF non-PO coding form is the page AP users open to code an invoice that has no purchase order to match against, assigning GL charge accounts, cost centers, projects, funds, and other segment values for posting into Oracle EBS Payables or Oracle Fusion Cloud Payables. In a typical Oracle WebCenter AP implementation, it is built as an Oracle Application Development Framework (ADF) page — JSF-based, built in JDeveloper, deployed to WebLogic Server alongside WebCenter Imaging and SOA Suite. Most implementations have customized it heavily over years — defaulting rules, validation hooks, fund accounting, encumbrance logic, segment lookups specific to the customer\'s chart of accounts.',
  },
  {
    q: 'Why is the ADF non-PO coding form so hard to maintain?',
    a: 'The ADF non-PO coding form is hard to maintain because the coding rules live in code rather than configuration, and the form is coupled to downstream SOA composites. Working confidently across JDeveloper, ADF Faces, ADF Business Components, and ADF Task Flows takes senior, hands-on experience with the stack. The coding rules tend to live in custom Java backing beans, page definitions, or business components rather than in configuration, so even small changes require a development cycle. And because the form is typically coupled to downstream SOA composites for approval routing and posting, changes ripple beyond the page itself.',
  },
  {
    q: 'Do we have to rebuild the entire form from scratch?',
    a: 'No. The EZ Cloud configurable coding form is already built — the engagement maps your existing rules into it, not a custom rebuild. The work is about reading your existing ADF page, extracting the coding rules and defaulting behavior, and mapping them into EZ Cloud configuration. The result is a coding experience that the AP team can change without a developer.',
  },
  {
    q: 'What about our EBS or Fusion integration?',
    a: 'The Oracle integration pattern is preserved on the same Oracle-supported interfaces your ERP team already audits. For Oracle E-Business Suite Payables customers, EZ Cloud writes coded invoices to the Payables Open Interface (AP_INVOICES_INTERFACE and AP_INVOICE_LINES_INTERFACE). For Oracle Fusion Cloud Payables customers, EZ Cloud posts via FBDI bulk import or REST through Oracle Integration Cloud. There is no unsupported back-channel write to the ERP.',
  },
  {
    q: 'We have custom Java in the ADF page backing beans. What happens to it?',
    a: 'Custom Java in your ADF backing beans is read directly during the engagement and classified into three buckets: configuration, configurable hooks, or retired. Rules that can be expressed as configuration in the EZ Cloud coding engine become configuration; rules that genuinely need custom logic are implemented as configurable hooks; rules that exist only to compensate for ADF limitations often go away entirely. The structured inventory of what your custom Java actually does is one of the deliverables of the engagement.',
  },
  {
    q: 'What is the typical timeline for an ADF non-PO coding form replacement?',
    a: 'Typical ADF non-PO coding form replacements run 3-6 months end to end. Duration depends on how customized the existing ADF form is, the complexity of the chart of accounts and fund structure, and how much of the surrounding SOA approval workflow is in scope. The reverse-engineering phase usually completes in the first 4-6 weeks and produces the structured inventory of coding rules; the configuration, integration, and parallel-run phases run from there.',
  },
  {
    q: 'Can we do this without disrupting our SOA approval workflow?',
    a: 'Yes — two common patterns let the replacement proceed without disrupting your SOA approval workflow. Pattern one: the EZ Cloud configurable approval engine takes over routing and the SOA composites that previously routed coded invoices are retired or simplified. Pattern two: the customer keeps the existing SOA approval composite, and EZ Cloud hands the coded invoice off to it through the same payload contract the ADF form previously used. Andrew reads the SOA layer as part of the engagement so the choice is made on facts about the existing composite, not assumptions.',
  },
  {
    q: 'Is Oracle ADF being deprecated?',
    a: 'No. Oracle ADF remains a strategic Oracle development framework and is supported alongside Oracle Fusion Middleware. Customers replace ADF non-PO coding forms not because of Oracle product end-of-life, but because of the maintenance burden of a heavily customized page where every coding rule change requires a development cycle. Modernization here is about giving the AP team a configurable experience, not about reacting to a deprecation date.',
  },
];

const OracleAdfNonPoCodingFormModernization = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle ADF Non-PO Coding Form Modernization — From Custom ADF to Configurable AP UI | EZ Cloud',
    description:
      'The Oracle ADF non-PO coding form is the most-customized piece of every WebCenter AP implementation — and the slowest to change. Every coding rule update requires an ADF development cycle. EZ Cloud replaces the ADF non-PO coding form with a modern, AP-team-configurable coding and approval experience, while preserving Oracle EBS or Fusion integration through Oracle-supported interfaces.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/oracle-adf-non-po-coding-form-modernization',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle Application Development Framework' },
      { '@type': 'Thing', name: 'Oracle ADF' },
      { '@type': 'Thing', name: 'Non-PO Coding Form' },
      { '@type': 'Thing', name: 'Oracle E-Business Suite Payables' },
      { '@type': 'Thing', name: 'Oracle Fusion Cloud Payables' },
      { '@type': 'Thing', name: 'Accounts Payable Coding' },
    ],
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to modernize an Oracle ADF non-PO coding form',
    description: 'How to replace the Oracle ADF non-PO coding form with a modern configurable coding and approval experience while preserving Oracle EBS or Fusion integration.',
    totalTime: 'P6M',
    step: DELIVERY_STEPS.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/oracle-adf-non-po-coding-form-modernization#step-' + (idx + 1),
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle ADF Non-PO Coding Form Modernization — From Custom ADF to Configurable AP UI | EZ Cloud"
        description="The Oracle ADF non-PO coding form is the most-customized piece of every WebCenter AP implementation — and the slowest to change. Every coding rule update requires an ADF development cycle. EZ Cloud replaces the ADF non-PO coding form with a modern, AP-team-configurable coding and approval experience, while preserving Oracle EBS or Fusion integration through Oracle-supported interfaces."
        canonical="https://ezcloud.ai/solutions/oracle-adf-non-po-coding-form-modernization"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Oracle ADF Non-PO Coding Form Modernization',
            url: 'https://ezcloud.ai/solutions/oracle-adf-non-po-coding-form-modernization',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Code2 className="w-4 h-4" aria-hidden />
              Oracle ADF · Non-PO coding form · AP-team configurable
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize your Oracle ADF non-PO coding form
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Every coding rule change is a development project. Modernize the non-PO coding
              experience without an ADF rebuild — same EBS or Fusion integration, AP-team-configurable
              UI.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('adf-coding-form-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an ADF Coding Form Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The ADF non-PO coding form, in plain language
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  "Our non-PO coding form is built in Oracle ADF and customizations turn every small
                  change into a development project."
                </strong>{' '}
                That is the verbatim sentence we hear most often from AP and Finance leaders running
                a WebCenter AP implementation. The non-PO coding form is the page AP users open when
                an invoice has no purchase order to match against, to assign charge accounts, cost
                centers, projects, funds, and other segment values for posting into Oracle EBS
                Payables or Oracle Fusion Cloud Payables.
              </p>
              <p>
                In a typical WebCenter AP implementation, that page is built in{' '}
                <strong className="text-gray-900">
                  Oracle Application Development Framework (ADF)
                </strong>{' '}
                — JSF-based UI, built in JDeveloper, deployed to WebLogic Server. Customers have
                customized it over the years with fund accounting, encumbrance, defaulting, and
                validation logic that ended up encoded in Java backing beans rather than
                configuration. That is what makes every change a development cycle.
              </p>
              <p>
                Modernization here means replacing the ADF coding UI with a modern,
                AP-team-configurable coding and approval experience — while preserving the Oracle
                integration layer (EBS Payables Open Interface or Fusion FBDI / OIC REST). The AP
                team configures coding rules through an admin UI rather than through a developer
                pulling JDeveloper out of mothballs.
              </p>
            </div>
          </div>
        </section>

        {/* What the ADF non-PO coding form is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What it is
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What the ADF non-PO coding form actually is
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four facts that frame the engagement. Each one shapes what the replacement project
              has to handle.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why it's the most painful piece */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Why customers raise it
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Why this is the most painful piece of WebCenter AP
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Across the customers we work with, the non-PO coding form is consistently the piece
              that comes up first. Four reinforcing reasons.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {PAIN_POINTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What modernization actually means here */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The definition
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
              What "modernization" actually means here
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Modernization of the ADF non-PO coding form is not a rewrite of the page in a newer
                ADF version, and it is not a UI skin on top of the same backing beans. It is the
                replacement of the ADF UI with a modern, configurable coding form — a coding
                experience where the rules live in configuration that the AP team can edit, not in
                Java code that has to be rebuilt and redeployed.
              </p>
              <p>
                The Oracle integration layer is preserved. Coded invoices still post to{' '}
                <strong className="text-gray-900">
                  Oracle EBS Payables via Payables Open Interface
                </strong>{' '}
                or to{' '}
                <strong className="text-gray-900">
                  Oracle Fusion Cloud Payables via FBDI or OIC REST
                </strong>
                . The ERP system of record, audit trail, segregation of duties, and security model
                stay where they are. The change is at the coding-and-approval layer, not at the ERP
                layer — and the result is that the AP team owns coding-rule changes instead of
                queuing them behind a JDeveloper sprint. See{' '}
                <Link to="/technology/native-erp" className="text-accent font-semibold">
                  ERP-native integration
                </Link>{' '}
                for how the integration layer is structured.
              </p>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where customers are when they raise this
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four scenarios we hear repeatedly. The shape of the engagement is different in each
              one, but the underlying need is the same: stop treating coding rule changes as a
              development project.
            </p>
            <div className="space-y-5">
              {SCENARIOS.map((s) => (
                <Card key={s.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{s.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How EZ Cloud delivers the replacement */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The delivery approach
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              How EZ Cloud delivers the replacement
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Six steps. The reverse-engineering of the existing ADF form is the part most
              consulting partners cannot do — the rest of the project depends on getting that
              inventory right.
            </p>
            <div className="space-y-5">
              {DELIVERY_STEPS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What stays the same */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What does not change
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What stays the same after the replacement
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Replacing the ADF coding UI does not require touching the ERP system of record, the
              audit trail, segregation of duties, the integration interface, or the security model.
              Those are explicitly preserved.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHAT_STAYS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on ADF coding form replacement
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Engagement options
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Two delivery modes. Standard Consulting is the default for customers who want a
              fixed-fee replacement project. Forward Deployed Engineering is the right fit when the
              existing ADF page is so heavily customized that reverse-engineering is the load-bearing
              part of the project.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 1 · ADF Coding Form Assessment
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-fee read of your existing ADF page
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Andrew reads your existing ADF non-PO coding form directly — page, backing beans,
                  ADF Business Components, custom Java, surrounding SOA composites — and delivers a
                  structured inventory of coding rules, defaulting behavior, validation hooks, and
                  approval routing. The inventory is yours to keep regardless of which delivery
                  partner you choose next.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 2 · Standard Consulting — Fixed-fee replacement project
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Scoped ADF-to-EZ-Cloud coding form replacement
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A defined-scope project that takes your existing ADF non-PO coding form, maps the
                  coding rules into EZ Cloud configuration, preserves the EBS Payables Open Interface
                  or Fusion FBDI / OIC integration pattern, runs in parallel during UAT, and hands
                  the coding configuration to your AP team at cutover. Project-based, founder-led,
                  scoped to a defined outcome.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 3 · Forward Deployed Engineering
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Heavy customization reverse-engineering
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  For implementations where the ADF page has been customized over many years —
                  extensive fund accounting, encumbrance, custom Java in backing beans, intricate
                  validation chains — Forward Deployed Engineering is the right mode. Andrew works
                  embedded with the customer team to reverse-engineer the page, extract the rules,
                  and rebuild on EZ Cloud configuration. Time-and-materials with a defined
                  reverse-engineering milestone before the build phase begins.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 4 · Strategic Advisory Retainer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Founder-level guidance during the decision window
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  For customers working through the ADF coding form decision in the context of a
                  larger WebCenter or EBS-to-Fusion conversation, a monthly retainer with Andrew
                  provides strategic guidance on architecture choices, sequencing, and how the
                  coding form decision interacts with{' '}
                  <Link to="/solutions/webcenter-12c-end-of-support-decision" className="text-accent font-semibold">
                    the December 2026 12c support decision
                  </Link>{' '}
                  and{' '}
                  <Link to="/solutions/webcenter-14c-upgrade" className="text-accent font-semibold">
                    the 14c upgrade path
                  </Link>
                  .
                </p>
              </Card>
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The ADF non-PO coding form decision rarely sits on its own — it usually shows up
              alongside a 12c support, 14c upgrade, or EBS-to-Fusion conversation. The Decision
              Guide is the structured framework for the four forward paths and how the coding form
              decision fits inside them.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the ADF non-PO coding form
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your ADF non-PO coding form
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist with 20+ years on the ADF, SOA, and WebCenter Imaging
              stack. Walk through your existing ADF coding form, the surrounding workflow, and the
              replacement options in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('adf-coding-form-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an ADF Coding Form Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleAdfNonPoCodingFormModernization;
